import type { ReactNode } from 'react';
import type React from 'react';
import { cn } from '@/lib/utils';

/**
 * La misma información de una tabla, apilada, para un teléfono.
 *
 * 🔴 El problema que resuelve, medido en un teléfono de 390 px: la tabla de
 * usuarios tiene ocho columnas. Entraban cuatro, y las otras cuatro —incluida la
 * de acciones— quedaban detrás de un scroll horizontal que nada anunciaba. En la
 * práctica, en un teléfono esa pantalla no se podía usar.
 *
 * **Por qué no una tabla que se transforma sola.** Se evaluó un componente que
 * recibiera columnas como datos y decidiera el formato. Habría que reescribir las
 * tablas existentes a una API de datos, y las celdas de acá no son texto: son
 * insignias, semáforos de vigencia, menús. Se pierde más de lo que se gana.
 *
 * Así que cada pantalla arma sus tarjetas y elige qué mostrar — que es lo
 * correcto, porque en una tarjeta no caben ocho campos y **hay que elegir**. La
 * tabla sigue viva de `md:` para arriba, sin tocarla.
 */

export interface CampoDeTarjeta {
  etiqueta: string;
  valor: ReactNode;
  /** Ocupa el ancho completo en vez de media columna. Para fechas con detalle. */
  ancho?: boolean;
}

export interface TarjetaMobile {
  /** Identidad estable de la fila. */
  id: string;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  /** Arriba a la derecha: el estado, normalmente una insignia. */
  insignia?: ReactNode;
  campos: CampoDeTarjeta[];
  /** El mismo menú de acciones de la fila. */
  acciones?: ReactNode;
  /**
   * Qué hacer al tocar la tarjeta. Sin esto la tarjeta es solo lectura.
   *
   * En un teléfono es AÚN más necesario que en la tabla: no hay fila que
   * recorrer con el ojo, la tarjeta es el elemento, y si no se puede tocar el
   * único camino queda escondido detrás del menú de tres puntos.
   */
  onOpen?: () => void;
  /**
   * El estado, como franja de color en el borde izquierdo.
   *
   * En una lista de tarjetas apiladas, la insignia de estado queda arriba a la
   * derecha de cada una: para saber cuáles están mal hay que leerlas todas. La
   * franja se ve **recorriendo la lista con el pulgar**, sin leer nada.
   *
   * No reemplaza a la insignia, la acompaña: un color solo no dice qué pasa, y
   * quien no distingue rojo de verde necesita la palabra igual.
   */
  estado?: 'bien' | 'atencion' | 'mal' | 'neutro';
}

interface MobileCardListProps {
  items: TarjetaMobile[];
  /** Qué decir cuando no hay nada. Mismo texto que el de la tabla. */
  vacio?: string;
  className?: string;
}

/* La franja del borde izquierdo, por estado. */
const FRANJA: Record<NonNullable<TarjetaMobile['estado']>, string> = {
  bien: 'before:bg-emerald-500',
  atencion: 'before:bg-amber-500',
  mal: 'before:bg-destructive',
  neutro: 'before:bg-border',
};

export function MobileCardList({
  items,
  vacio = 'Sin resultados',
  className,
}: MobileCardListProps) {
  if (items.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{vacio}</p>;
  }

  return (
    <ul className={cn('space-y-3', className)}>
      {items.map((item) => (
        <li
          key={item.id}
          {...(item.onOpen
            ? {
                role: 'link',
                tabIndex: 0,
                onClick: item.onOpen,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    item.onOpen?.();
                  }
                },
              }
            : {})}
          className={cn(
            'relative overflow-hidden rounded-lg border border-border bg-card p-3',
            /*
              La franja va como pseudo-elemento y no como `border-left`: un borde
              de 3 px desplazaría el contenido de las tarjetas con estado respecto
              de las que no lo tienen, y la lista quedaría desalineada.
            */
            item.estado &&
              cn(
                'pl-4 before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-[""]',
                FRANJA[item.estado]
              ),
            item.onOpen &&
              'cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {/* `break-words`: los correos largos desbordaban la tarjeta. */}
              <p className="break-words font-medium leading-tight">{item.titulo}</p>
              {item.subtitulo && (
                <p className="mt-0.5 break-words text-xs text-muted-foreground">{item.subtitulo}</p>
              )}
            </div>
            {/* Tocar el menú no abre la tarjeta: si no, se cerraría solo. */}
            <div
              className="flex shrink-0 items-center gap-1"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {item.insignia}
              {item.acciones}
            </div>
          </div>

          {item.campos.length > 0 && (
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border pt-3 text-sm">
              {item.campos.map((campo) => (
                <div key={campo.etiqueta} className={campo.ancho ? 'col-span-2' : undefined}>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    {campo.etiqueta}
                  </dt>
                  <dd className="mt-0.5 break-words">{campo.valor}</dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ul>
  );
}

export default MobileCardList;
